import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { ThemeProvider as ThemeProviderV5, createTheme as createThemeV5 } from "@mui/material/styles";
import moment from "moment";

import GetAppIcon from "@material-ui/icons/GetApp";
import EventIcon from "@material-ui/icons/Event";
import WarningIcon from "@material-ui/icons/Warning";
import SentimentSatisfiedAltIcon from "@material-ui/icons/SentimentSatisfiedAlt";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import HowToRegIcon from "@material-ui/icons/HowToReg";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
// SentimentNeutral e PendingActions nao existem no @material-ui/icons
// (v4), so' no pacote v5.
import { PendingActions as PendingActionsIcon, SentimentNeutral as SentimentNeutralIcon } from "@mui/icons-material";

import useDashboard from "../../../hooks/useDashboard";
import api from "../../../services/api";
import AttendantCoachingModal from "../../../components/Dashboard/AttendantCoachingModal";
import CardCounter from "../../../components/Dashboard/CardCounter";
import ChartsDate from "../../../pages/Dashboard/ChartsDate";
import { ChatsUser } from "../../../pages/Dashboard/ChartsUser";
import SobrecargaChart from "../../../pages/Dashboard/SobrecargaChart";
import AttendanceReportTab from "../../../pages/Dashboard/AttendanceReportTab";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { hasPermission } from "../../../helpers/hasPermission";
import { formatTime, getDicas, computeTeamAverages } from "../../../utils/attendantCoaching";

const PERIODS = [
  { key: "hoje", label: "Hoje" },
  { key: "7dias", label: "7 dias" },
  { key: "30dias", label: "30 dias" },
  { key: "custom", label: "Personalizado" },
];

// Mesmas 3 abas do Dashboard Classico (pages/Dashboard/index.js) - a
// auditoria pedida pelo usuario 2026-10-22 ("mapeie tudo o que tem no
// layout antigo") achou que so' a aba "Visao geral" tinha sido
// construida aqui; mapa de calor, reincidencia por motivo/empresa e
// sobrecarga de atendentes (as 3 coisas que ele apontou) vivem nas
// outras duas abas, que ja existiam prontas no Classico - reaproveitadas
// abaixo (SobrecargaChart/AttendanceReportTab/ChatsUser/RankingPodium/
// ChartsDate), nao reescritas.
const DASHBOARD_TABS = [
  { key: "overview", label: "Visão geral", permission: "dashboard:tab:overview" },
  { key: "quality", label: "Qualidade & carga", permission: "dashboard:tab:quality" },
  { key: "detailed", label: "Relatório detalhado", permission: "dashboard:tab:detailed" },
];

const NPS_COLORS = { success: "#1B6B3A", neutral: "#5E5C70", danger: "#B42318" };

const periodRange = (period, customStart, customEnd) => {
  const today = moment().format("YYYY-MM-DD");
  if (period === "7dias") return { from: moment().subtract(6, "days").format("YYYY-MM-DD"), to: today };
  if (period === "30dias") return { from: moment().subtract(29, "days").format("YYYY-MM-DD"), to: today };
  if (period === "custom") return { from: customStart || today, to: customEnd || today };
  return { from: today, to: today };
};

const MetricCard = ({ label, value, hint, tokens, children }) => (
  <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
    <span style={{ fontSize: 13, color: tokens.textMuted, fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: tokens.text }}>{value}</span>
    {children}
    {hint && <span style={{ fontSize: 12, color: tokens.textMuted }}>{hint}</span>}
  </div>
);

const situacaoChip = (dicas, tokens) => {
  const hasWarning = dicas.some((d) => d.color === "warning");
  const hasError = dicas.some((d) => d.color === "error");
  if (hasError || hasWarning) {
    return { label: dicas.find((d) => d.color === "error" || d.color === "warning").text, bg: "#FFF1E6", fg: "#9A3F07" };
  }
  if (dicas.some((d) => d.text === "Dentro da média")) {
    return { label: "Dentro da média", bg: "#EEF9F2", fg: "#1B6B3A" };
  }
  return { label: "Poucos dados", bg: tokens.neutralChipBg, fg: tokens.neutralChipText };
};

// Tela "Dashboard" do layout Novo (projeto 2026-10-21, ver PROMPT.md em
// D:\voicechat\novo layout). Tarefa #62. A aba "Visão geral" segue o
// mockup proprio (cards + tabela com tokens do layout Novo); as abas
// "Qualidade & carga" e "Relatório detalhado" nao tinham mockup, mas
// tem logica real demais (sobrecarga, reincidencia por motivo/empresa,
// mapa de calor de horario) pra reescrever do zero sem risco - reusam
// os componentes do Classico tal como sao (RankingPodium/ChartsDate em
// v4, SobrecargaChart/AttendanceReportTab/ChatsUser em v5, com bridge
// de tema pro v5 igual o Dashboard Classico ja faz). Dados via
// useDashboard().find() - mesmo hook do Classico; "Situação"/"Ver
// dicas" reaproveitam getDicas/AttendantCoachingModal, extraidos de
// TableAttendantsStatus.js pra um util compartilhado
// (utils/attendantCoaching.js) em vez de duplicados.
const NewDashboard = () => {
  const theme = useTheme();
  const tokens = theme.newLayoutTokens;
  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  // Bridge pro MUI v5 (RankingPodium/SobrecargaChart/AttendanceReportTab
  // sao v5, igual o Dashboard Classico ja faz pros mesmos componentes -
  // mesmo padrao, so' que a partir do tema Novo em vez do tema Classico.
  const themeV5 = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: theme.palette.type === "dark" ? "dark" : "light",
          primary: { main: theme.palette.primary.main },
          background: { default: tokens.bg, paper: tokens.surface },
        },
        shape: { borderRadius: tokens.radii.control },
      }),
    [theme.palette.type, theme.palette.primary.main, tokens]
  );

  const visibleTabs = useMemo(() => DASHBOARD_TABS.filter((t) => hasPermission(user, t.permission)), [user]);
  const [tab, setTab] = useState(0);
  const activeTabKey = visibleTabs[Math.min(tab, visibleTabs.length - 1)]?.key;

  const [period, setPeriod] = useState("hoje");
  const [customStart, setCustomStart] = useState(moment().format("YYYY-MM-DD"));
  const [customEnd, setCustomEnd] = useState(moment().format("YYYY-MM-DD"));
  const [counters, setCounters] = useState({});
  const [ratingEnabled, setRatingEnabled] = useState(true);
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coachingTarget, setCoachingTarget] = useState(null);
  const [overloaded, setOverloaded] = useState([]);

  const { from, to } = useMemo(() => periodRange(period, customStart, customEnd), [period, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await find({ date_from: from, date_to: to });
      setCounters(data?.counters || {});
      setRatingEnabled(data?.ratingEnabled !== false);
      // Pedido explicito do usuario 2026-10-22: "Desempenho por atendente"
      // mostra so' quem esteve ativo (teve atendimento) no periodo
      // selecionado - quem nao atendeu nada nesse recorte nao entra na
      // tabela. (Tentativa anterior de mostrar todo mundo foi revertida
      // a pedido dele.)
      setAttendants((data?.attendants || []).filter((a) => (a.tickets || 0) > 0));
    } finally {
      setLoading(false);
    }
  }, [find, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Alerta de sobrecarga (tarefa apontada pelo usuario 2026-10-22: "quando
  // o atendente esta sobrecarregado" nao aparecia em lugar nenhum aqui) -
  // mesmo endpoint/logica do Dashboard Classico, so' reaproveitado.
  const fetchOverload = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/tecnicos-sobrecarga", {
        params: { dataInicial: from, dataFinal: to },
      });
      setOverloaded((data?.tecnicos || []).filter((t) => t.status === "Sobrecarregado"));
    } catch (err) {
      setOverloaded([]);
    }
  }, [from, to]);

  useEffect(() => {
    fetchOverload();
  }, [fetchOverload]);

  const teamAvg = useMemo(() => computeTeamAverages(attendants), [attendants]);
  const total = (counters.supportHappening || 0) + (counters.supportPending || 0) + (counters.supportFinished || 0);
  const finishedPct = total ? (counters.supportFinished / total) * 100 : 0;
  const happeningPct = total ? (counters.supportHappening / total) * 100 : 0;

  const leaderId = useMemo(() => {
    if (!attendants.length) return null;
    return [...attendants].sort((a, b) => (b.tickets || 0) - (a.tickets || 0) || (a.avgWaitTime || 0) - (b.avgWaitTime || 0))[0]?.name;
  }, [attendants]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", justifyContent: "space-between" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: "-0.01em", color: tokens.text }}>Desempenho do time</h1>
          <span style={{ fontSize: 14, color: tokens.textMuted }}>Atualizado em tempo real</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div role="group" aria-label="Período" style={{ display: "flex", padding: 4, background: tokens.surface, border: `1px solid ${tokens.borderInput}`, borderRadius: 12, gap: 2 }}>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                aria-pressed={period === p.key}
                onClick={() => setPeriod(p.key)}
                style={{
                  height: 32,
                  padding: "0 14px",
                  border: "none",
                  borderRadius: 8,
                  background: period === p.key ? tokens.primarySoft : "transparent",
                  color: period === p.key ? tokens.primaryDark : tokens.neutralChipText,
                  fontSize: 13,
                  fontWeight: period === p.key ? 600 : 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {p.key === "custom" && <EventIcon style={{ fontSize: 14 }} />}
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={{ height: 40, padding: "0 10px", border: `1px solid ${tokens.borderInput}`, borderRadius: 10, fontSize: 13 }} />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={{ height: 40, padding: "0 10px", border: `1px solid ${tokens.borderInput}`, borderRadius: 10, fontSize: 13 }} />
            </>
          )}
          <button
            style={{ height: 42, padding: "0 14px", border: `1px solid ${tokens.borderInput}`, borderRadius: 12, background: tokens.surface, color: tokens.text2, fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
            onClick={() => window.print()}
          >
            <GetAppIcon fontSize="small" /> Exportar
          </button>
        </div>
      </div>

      {overloaded.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px", borderRadius: 12, background: "#FFF8EB", border: "1px solid #F5D9A8", color: "#8A5300", fontSize: 13.5 }}>
          <WarningIcon style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }} />
          <span>
            {overloaded.length === 1
              ? `${overloaded[0].name} está com volume de atendimentos simultâneos acima do normal. Considere redistribuir atendimentos.`
              : `${overloaded.length} atendentes estão com volume de atendimentos simultâneos acima do normal (${overloaded.map((t) => t.name).join(", ")}). Considere redistribuir atendimentos.`}
          </span>
        </div>
      )}

      <div role="tablist" aria-label="Seções do Dashboard" style={{ display: "flex", gap: 24, borderBottom: `1px solid ${tokens.borderInput}` }}>
        {visibleTabs.map((t, idx) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === idx}
            onClick={() => setTab(idx)}
            style={{
              padding: "10px 0",
              border: "none",
              background: "transparent",
              borderBottom: `2px solid ${tab === idx ? tokens.primary : "transparent"}`,
              fontSize: 14,
              fontWeight: tab === idx ? 600 : 500,
              color: tab === idx ? tokens.primaryDark : tokens.neutralChipText,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {visibleTabs.length === 0 && (
        <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, padding: 20, color: tokens.textMuted }}>
          Nenhuma aba liberada para o seu usuário — peça a um administrador pra liberar em Usuários → Permissões.
        </div>
      )}

      {activeTabKey === "overview" && (
      <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <MetricCard tokens={tokens} label="Atendimentos no período" value={loading ? "…" : total}>
          <div style={{ height: 8, borderRadius: 4, background: tokens.dividerStrong, display: "flex", overflow: "hidden" }}>
            <span style={{ width: `${finishedPct}%`, background: "#1B6B3A" }} />
            <span style={{ width: `${happeningPct}%`, background: tokens.primary }} />
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: tokens.neutralChipText }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "#1B6B3A" }} />{counters.supportFinished || 0} finalizados</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: tokens.primary }} />{counters.supportHappening || 0} em atendimento</span>
            <span>{counters.supportPending || 0} aguardando</span>
          </div>
        </MetricCard>
        <MetricCard tokens={tokens} label="Tempo médio de espera" value={loading ? "…" : formatTime(counters.avgWaitTime)} hint="até o primeiro atendente responder" />
        <MetricCard tokens={tokens} label="Tempo médio de atendimento" value={loading ? "…" : formatTime(counters.avgSupportTime)} hint="da abertura ao encerramento" />
        <MetricCard
          tokens={tokens}
          label="Satisfação (NPS)"
          value={counters.withRating ? counters.npsScore || 0 : "—"}
          hint={counters.withRating ? undefined : "nenhuma avaliação no período"}
        />
      </div>

      <section style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "20px 20px 12px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: tokens.text }}>Desempenho por atendente</h2>
          <span style={{ fontSize: 12, color: tokens.textMuted }}>Médias consideram quem fez pelo menos 3 atendimentos</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 820, borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: tokens.textMuted, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <th style={{ padding: "10px 20px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}` }}>Atendente</th>
                <th style={{ padding: "10px 12px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}`, textAlign: "right" }}>Atendimentos</th>
                <th style={{ padding: "10px 12px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}`, textAlign: "right" }}>Espera média</th>
                <th style={{ padding: "10px 12px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}`, textAlign: "right" }}>Atendimento médio</th>
                <th style={{ padding: "10px 12px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}` }}>Nota</th>
                <th style={{ padding: "10px 12px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}` }}>Situação</th>
                <th style={{ padding: "10px 20px", fontWeight: 600, borderBottom: `1px solid ${tokens.divider}`, textAlign: "right" }}>Coaching IA</th>
              </tr>
            </thead>
            <tbody>
              {attendants.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: tokens.textMuted }}>Nenhum atendimento no período</td></tr>
              )}
              {attendants.map((a, idx) => {
                const dicas = getDicas(a, teamAvg);
                const chip = situacaoChip(dicas, tokens);
                const isLeader = a.name === leaderId;
                const isLast = idx === attendants.length - 1;
                return (
                  <tr key={a.id || a.name}>
                    <td style={{ padding: "14px 20px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ position: "relative", width: 36, height: 36, borderRadius: 18, background: tokens.primarySoft, color: tokens.primaryText, fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {(a.name || "?").slice(0, 2).toUpperCase()}
                          <span style={{ position: "absolute", right: -2, bottom: -2, width: 12, height: 12, borderRadius: 6, background: a.online ? "#22A55B" : "#A3A1B3", border: `2px solid ${tokens.surface}` }} />
                        </span>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span style={{ fontWeight: 600, color: tokens.text }}>{a.name}</span>
                          {isLeader ? (
                            <span style={{ fontSize: 12, color: "#9A6A00", fontWeight: 600 }}>🏆 Mais atendimentos · menor espera</span>
                          ) : (
                            <span style={{ fontSize: 12, color: tokens.textMuted }}>{a.online ? "online agora" : "offline"}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 12px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}`, textAlign: "right", fontWeight: 600, color: tokens.text }}>{a.tickets}</td>
                    <td style={{ padding: "14px 12px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}`, textAlign: "right", color: tokens.text2 }}>{formatTime(a.avgWaitTime)}</td>
                    <td style={{ padding: "14px 12px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}`, textAlign: "right", color: tokens.text2 }}>{formatTime(a.avgSupportTime)}</td>
                    <td style={{ padding: "14px 12px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}`, color: tokens.textMuted }}>{a.rating ? a.rating : "sem avaliações"}</td>
                    <td style={{ padding: "14px 12px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}` }}>
                      <span style={{ padding: "4px 10px", borderRadius: 12, background: chip.bg, color: chip.fg, fontSize: 12, fontWeight: 600 }}>{chip.label}</span>
                    </td>
                    <td style={{ padding: "14px 20px", borderBottom: isLast ? "none" : `1px solid ${tokens.dividerStrong}`, textAlign: "right" }}>
                      <button
                        onClick={() => setCoachingTarget(a)}
                        style={{ height: 32, padding: "0 12px", border: `1px solid ${tokens.primarySofterBorder}`, borderRadius: 8, background: tokens.primarySofter, color: tokens.primaryText, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                      >
                        Ver dicas
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, padding: 20 }}>
        <h2 style={{ margin: "0 0 12px", fontSize: 17, fontWeight: 600, color: tokens.text }}>Atendimentos por dia</h2>
        <ChartsDate dateStart={from} dateEnd={to} />
      </section>
      </>
      )}

      {activeTabKey === "quality" && (
        <ThemeProviderV5 theme={themeV5}>
          <SobrecargaChart dateStart={from} dateEnd={to} activeOrOverloadedOnly />

          <div style={{ marginTop: 24 }}>
            <h2 style={{ margin: "0 0 12px", fontSize: 17, fontWeight: 600, color: tokens.text, fontFamily: "Inter, system-ui, sans-serif" }}>
              Avaliações (NPS)
            </h2>
            {!ratingEnabled ? (
              <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, padding: 16, color: tokens.textMuted, fontSize: 13.5 }}>
                A avaliação de atendimento (NPS) está desativada nas configurações da empresa — por isso não há cards aqui.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
                <CardCounter icon={<SentimentSatisfiedAltIcon fontSize="inherit" />} color={NPS_COLORS.success} title="Promotores" value={`${counters.npsPromotersPerc || 0}%`} loading={loading} />
                <CardCounter icon={<SentimentNeutralIcon fontSize="inherit" />} color={NPS_COLORS.neutral} title="Neutros" value={`${counters.npsPassivePerc || 0}%`} loading={loading} />
                <CardCounter icon={<SentimentDissatisfiedIcon fontSize="inherit" />} color={NPS_COLORS.danger} title="Detratores" value={`${counters.npsDetractorsPerc || 0}%`} loading={loading} />
                <CardCounter icon={<HowToRegIcon fontSize="inherit" />} color={tokens.primary} title="Avaliações Recebidas" value={counters.withRating || 0} loading={loading} />
                <CardCounter icon={<PendingActionsIcon fontSize="inherit" />} color={NPS_COLORS.neutral} title="Sem Avaliação" value={counters.withoutRating || 0} loading={loading} />
                <CardCounter icon={<HourglassEmptyIcon fontSize="inherit" />} color={tokens.primary} title="Aguardando Avaliação" value={counters.waitRating || 0} loading={loading} />
              </div>
            )}
          </div>
        </ThemeProviderV5>
      )}

      {activeTabKey === "detailed" && (
        <ThemeProviderV5 theme={themeV5}>
          <AttendanceReportTab dateStart={from} dateEnd={to} />

          <h2 style={{ margin: "24px 0 12px", fontSize: 17, fontWeight: 600, color: tokens.text, fontFamily: "Inter, system-ui, sans-serif" }}>
            Atendimentos por atendente
          </h2>
          <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, padding: 16 }}>
            <ChatsUser dateStart={from} dateEnd={to} />
          </div>
        </ThemeProviderV5>
      )}

      {coachingTarget && (
        <AttendantCoachingModal open onClose={() => setCoachingTarget(null)} attendant={coachingTarget} />
      )}
    </div>
  );
};

export default NewDashboard;
